import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { classes } from "@/data/classes";

/**
 * Reusable admin notification kinds. Add new kinds here for future modules
 * (field trips, volunteer sign-ups, instructor applications, membership
 * payments, donations, refund disputes, general system alerts). Consumers
 * only need to update icon/label mappings in NotificationCenter.tsx.
 */
export type NotificationKind =
  // pop-up
  | "vendor_new"
  | "vendor_paid"
  | "vendor_pending"
  | "vendor_canceled"
  | "ticket_new"
  | "ticket_paid"
  | "ticket_unsent"
  | "payment_failed"
  // classes
  | "class_new"
  | "class_paid"
  | "class_pending"
  | "class_failed"
  | "class_canceled"
  | "class_refunded"
  | "class_interest"
  | "class_near_full"
  | "class_full"
  | "class_waitlist_new"
  | "class_waitlist_available"
  | "class_email_failed"
  // email delivery
  | "email_failed"
  | "email_stuck"
  | "email_resent"
  | "sender_domain_error"
  // instructor proposals
  | "proposal_new";

export type NotificationItemType =
  | "popup_vendor"
  | "popup_ticket"
  | "payment"
  | "interest"
  | "class_capacity"
  | "email"
  | "instructor_proposal";

export interface AdminNotification {
  id: string; // unique key: `${itemType}:${itemId}`
  itemType: NotificationItemType;
  itemId: string;
  kind: NotificationKind;
  title: string;
  customerName: string;
  detail: string;
  paymentStatus: string;
  createdAt: string;
  read: boolean;
  /** For class registrations — used for click-through navigation */
  classId?: string;
  session?: string;
  amount?: number;
  studentName?: string;
  /** For email notifications — used for resend action */
  messageId?: string;
  emailTemplate?: string;
  emailError?: string;
  emailAttemptCount?: number;
  relatedItemType?: string | null;
  relatedItemId?: string | null;
}

interface EmailLogRow {
  id: string;
  message_id: string | null;
  template_name: string;
  recipient_email: string;
  status: string;
  error_message: string | null;
  metadata: any;
  related_item_type: string | null;
  related_item_id: string | null;
  attempt_count: number | null;
  resend_of_message_id: string | null;
  created_at: string;
}

interface VendorRow {
  id: string;
  full_name: string;
  email: string;
  vendor_option: string;
  amount_due: number;
  payment_status: string;
  registration_status: string;
  review_status: "New" | "Reviewed";
  reviewed_at: string | null;
  reviewed_by: string | null;
  created_at: string;
}

interface TicketRow {
  id: string;
  full_name: string;
  email: string;
  ticket_quantity: number;
  amount_due: number;
  payment_status: string;
  ticket_status: string;
  created_at: string;
}

interface ClassPaymentRow {
  id: string;
  stripe_session_id: string | null;
  class_id: string;
  adult_name: string | null;
  email: string | null;
  child_name: string | null;
  child_age: number | null;
  selected_sessions: any;
  amount_paid: number | null;
  payment_status: string | null;
  hidden: boolean | null;
  created_at: string;
}

interface ClassInterestRow {
  id: string;
  class_id: string;
  adult_name: string;
  email: string;
  child_name: string;
  child_age: number;
  created_at: string;
}

interface ProposalRow {
  id: string;
  full_name: string;
  email: string;
  proposed_class_name: string;
  status: string;
  review_status: "New" | "Reviewed";
  created_at: string;
}

const money = (c: number) => `$${(c / 100).toFixed(2)}`;

const CLASS_CAPACITY_DEFAULT = 10;
const NEAR_FULL_DEFAULT = 3;

const NEAR_FULL_STORAGE_KEY = "admin.nearFullThreshold";
const getNearFullThreshold = (): number => {
  if (typeof window === "undefined") return NEAR_FULL_DEFAULT;
  const raw = window.localStorage.getItem(NEAR_FULL_STORAGE_KEY);
  const n = raw ? parseInt(raw, 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : NEAR_FULL_DEFAULT;
};

function parseSessions(raw: any): string[] {
  if (!raw) return [];
  try {
    const val = typeof raw === "string" ? JSON.parse(raw) : raw;
    if (Array.isArray(val)) return val.map(String);
    if (val) return [String(val)];
  } catch {
    if (typeof raw === "string") return [raw];
  }
  return [];
}

function sessionLabelToDisplay(label: string): string {
  const num = parseInt(label.match(/\d+/)?.[0] ?? "", 10);
  switch (num) {
    case 1: return "May 2026";
    case 2: return "June 2026";
    case 3: return "July 2026";
    case 4: return "August 2026";
    default: return label;
  }
}

function classTitle(id: string): string {
  return classes.find((c) => c.id === id)?.title ?? id;
}

function buildVendorNotification(v: VendorRow): AdminNotification {
  const option = v.vendor_option === "rent" ? "Table Rental" : "Bring Own Table";
  let kind: NotificationKind = "vendor_new";
  let title = "New Vendor Registration";
  if (v.payment_status === "canceled" || v.registration_status === "canceled") {
    kind = "vendor_canceled";
    title = "Vendor Canceled";
  } else if (v.payment_status === "paid") {
    kind = "vendor_paid";
    title = "Vendor Payment Received";
  } else if (v.payment_status === "pending") {
    kind = "vendor_pending";
    title = "Vendor Payment Pending";
  }
  return {
    id: `popup_vendor:${v.id}`,
    itemType: "popup_vendor",
    itemId: v.id,
    kind,
    title,
    customerName: v.full_name,
    detail: `${option} (${money(v.amount_due)})`,
    paymentStatus: v.payment_status,
    createdAt: v.created_at,
    read: false,
  };
}

function buildTicketNotification(t: TicketRow): AdminNotification {
  let kind: NotificationKind = "ticket_new";
  let title = "New Early Bird Ticket Purchase";
  if (t.payment_status === "canceled" || t.ticket_status === "canceled") {
    kind = "vendor_canceled";
    title = "Ticket Canceled";
  } else if (t.payment_status === "paid" && t.ticket_status !== "sent") {
    kind = "ticket_unsent";
    title = "Ticket Awaiting Email";
  } else if (t.payment_status === "paid") {
    kind = "ticket_paid";
    title = "Ticket Payment Received";
  }
  return {
    id: `popup_ticket:${t.id}`,
    itemType: "popup_ticket",
    itemId: t.id,
    kind,
    title,
    customerName: t.full_name,
    detail: `${t.ticket_quantity} ticket${t.ticket_quantity !== 1 ? "s" : ""} (${money(t.amount_due)})`,
    paymentStatus: t.payment_status,
    createdAt: t.created_at,
    read: false,
  };
}

function buildClassPaymentNotification(p: ClassPaymentRow): AdminNotification {
  const status = (p.payment_status ?? "pending").toLowerCase();
  const sessions = parseSessions(p.selected_sessions).map(sessionLabelToDisplay).join(", ");
  let kind: NotificationKind = "class_new";
  let title = "New Class Registration";
  if (status === "paid" || status === "complete" || status === "succeeded") {
    kind = "class_paid";
    title = "Class Payment Received";
  } else if (status === "failed") {
    kind = "class_failed";
    title = "Class Payment Failed";
  } else if (status === "canceled" || status === "cancelled") {
    kind = "class_canceled";
    title = "Class Registration Canceled";
  } else if (status === "refunded") {
    kind = "class_refunded";
    title = "Class Payment Refunded";
  } else if (status === "pending" || status === "unpaid") {
    kind = "class_pending";
    title = "Class Payment Pending";
  }
  const amount = p.amount_paid ?? 0;
  const detail = [
    classTitle(p.class_id),
    sessions,
    amount ? money(amount) + (kind === "class_paid" ? " paid" : "") : null,
  ]
    .filter(Boolean)
    .join(" • ");
  return {
    id: `payment:${p.id}`,
    itemType: "payment",
    itemId: p.stripe_session_id ?? p.id,
    kind,
    title,
    customerName: p.adult_name || p.email || "Unknown",
    detail,
    paymentStatus: status,
    createdAt: p.created_at,
    read: false,
    classId: p.class_id,
    session: sessions,
    amount,
    studentName: p.child_name ?? undefined,
  };
}

function buildClassInterestNotification(i: ClassInterestRow): AdminNotification {
  return {
    id: `interest:${i.id}`,
    itemType: "interest",
    itemId: i.id,
    kind: "class_interest",
    title: "New Class Interest",
    customerName: i.adult_name,
    detail: `${classTitle(i.class_id)} — ${i.child_name} (age ${i.child_age})`,
    paymentStatus: "interest",
    createdAt: i.created_at,
    read: false,
    classId: i.class_id,
    studentName: i.child_name,
  };
}

function tryBrowserNotify(title: string, body: string) {
  try {
    if (typeof window === "undefined" || !("Notification" in window)) return;
    if (Notification.permission === "granted") {
      new Notification(title, { body });
    }
  } catch {
    /* ignore */
  }
}

const SENDER_DOMAIN_ERROR_PATTERNS = [
  /no[_ ]matching[_ ]sender/i,
  /sender.*not verified/i,
  /domain.*not verified/i,
  /verify.*domain/i,
  /no email domain record/i,
  /authentication failed/i,
  /forbidden.*from/i,
];

export function isSenderDomainError(msg?: string | null): boolean {
  if (!msg) return false;
  return SENDER_DOMAIN_ERROR_PATTERNS.some((r) => r.test(msg));
}

const EMAIL_TYPE_LABELS: Record<string, string> = {
  "class-registration-confirmation": "Class Registration Confirmation",
  "interest-welcome": "Interest Form Welcome",
  "popup-vendor-confirmation": "Pop-Up Vendor Confirmation",
  "popup-early-bird-confirmation": "Early Bird Ticket",
};

function emailTypeLabel(t: string): string {
  return EMAIL_TYPE_LABELS[t] ?? t;
}

function buildEmailNotification(e: EmailLogRow): AdminNotification | null {
  const status = (e.status ?? "").toLowerCase();
  let kind: NotificationKind = "email_failed";
  let title = "Email Delivery Failed";
  // Archived backlog from the retired sender domain: preserved for audit, never actionable.
  if (status === "expired_due_to_sender_setup") {
    return null;
  }
  if (status === "sent" && e.resend_of_message_id) {
    kind = "email_resent";
    title = "Email Resent Successfully";
  } else if (isSenderDomainError(e.error_message)) {
    kind = "sender_domain_error";
    title = "Sender Domain Not Verified";
  } else if (status === "dlq" || status === "failed" || status === "bounced" || status === "complained") {

    kind = "email_failed";
    title = status === "bounced" ? "Email Bounced"
          : status === "complained" ? "Email Complaint"
          : "Email Delivery Failed";
  } else {
    return null;
  }
  return {
    id: `email:${e.message_id ?? e.id}`,
    itemType: "email",
    itemId: e.message_id ?? e.id,
    kind,
    title,
    customerName: e.recipient_email,
    detail: `${emailTypeLabel(e.template_name)} — ${e.error_message ?? "Delivery failed"}`,
    paymentStatus: status,
    createdAt: e.created_at,
    read: false,
    messageId: e.message_id ?? undefined,
    emailTemplate: e.template_name,
    emailError: e.error_message ?? undefined,
    emailAttemptCount: e.attempt_count ?? 1,
    relatedItemType: e.related_item_type,
    relatedItemId: e.related_item_id,
  };
}


export function useAdminNotifications(userId: string | undefined) {
  const [vendors, setVendors] = useState<VendorRow[]>([]);
  const [emailLogs, setEmailLogs] = useState<EmailLogRow[]>([]);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [classPayments, setClassPayments] = useState<ClassPaymentRow[]>([]);
  const [classInterest, setClassInterest] = useState<ClassInterestRow[]>([]);
  const [proposals, setProposals] = useState<ProposalRow[]>([]);
  const [readMarkers, setReadMarkers] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [nearFullThreshold, setNearFullThresholdState] = useState<number>(getNearFullThreshold);
  const seededRef = useRef(false);

  const markerKey = (type: string, id: string) => `${type}:${id}`;

  const setNearFullThreshold = useCallback((n: number) => {
    setNearFullThresholdState(n);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(NEAR_FULL_STORAGE_KEY, String(n));
    }
  }, []);

  const fetchAll = useCallback(async () => {
    const [{ data: v }, { data: t }, { data: cp }, { data: ci }, { data: m }, { data: el }, { data: pr }] = await Promise.all([
      supabase
        .from("popup_vendor_registrations")
        .select("id, full_name, email, vendor_option, amount_due, payment_status, registration_status, review_status, reviewed_at, reviewed_by, created_at")
        .order("created_at", { ascending: false }),
      supabase
        .from("popup_early_bird_orders")
        .select("id, full_name, email, ticket_quantity, amount_due, payment_status, ticket_status, created_at")
        .order("created_at", { ascending: false }),
      supabase
        .from("class_payments")
        .select("id, stripe_session_id, class_id, adult_name, email, child_name, child_age, selected_sessions, amount_paid, payment_status, hidden, created_at")
        .or("hidden.is.null,hidden.eq.false")
        .order("created_at", { ascending: false }),
      supabase
        .from("class_interest")
        .select("id, class_id, adult_name, email, child_name, child_age, created_at")
        .order("created_at", { ascending: false }),
      supabase.from("admin_read_markers").select("item_type,item_id"),
      supabase
        .from("email_send_log" as any)
        .select("id, message_id, template_name, recipient_email, status, error_message, metadata, related_item_type, related_item_id, attempt_count, resend_of_message_id, created_at")
        .order("created_at", { ascending: false })
        .limit(500),
      supabase
        .from("instructor_proposals" as any)
        .select("id, full_name, email, proposed_class_name, status, review_status, created_at")
        .order("created_at", { ascending: false }),
    ]);
    setVendors((v as any) || []);
    setTickets((t as any) || []);
    setClassPayments((cp as any) || []);
    setClassInterest((ci as any) || []);
    setReadMarkers(new Set((m || []).map((x: any) => markerKey(x.item_type, x.item_id))));
    setEmailLogs((el as any) || []);
    setProposals((pr as any) || []);
    setLoading(false);
    seededRef.current = true;
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Realtime subscriptions (single channel, all tables)
  //
  // IMPORTANT: every hook instance MUST subscribe to a UNIQUE channel topic.
  // Supabase's realtime client caches channels by topic name, so if two mounts
  // of this hook (e.g. AdminShell + UnifiedDashboard) reuse the same topic,
  // the second `.on("postgres_changes", ...)` call runs against an
  // already-subscribed channel and throws:
  //   "cannot add postgres_changes callbacks ... after subscribe()"
  // That error unmounts the consumer tree and blanks the admin dashboard.
  // Appending a random suffix guarantees each mount gets its own channel.
  // See src/hooks/__tests__/useAdminNotifications.channel.test.ts.
  useEffect(() => {
    const channel = supabase
      .channel(`admin-notifications-${Math.random().toString(36).slice(2)}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "popup_vendor_registrations" },
        (payload) => {
          setVendors((prev) => {
            const row = (payload.new || payload.old) as VendorRow;
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as VendorRow;
            else next.unshift(payload.new as VendorRow);
            return next;
          });
          if (payload.eventType === "INSERT" && seededRef.current) {
            const row = payload.new as VendorRow;
            tryBrowserNotify("Silver Sage Homeschool Hub", `New vendor registration from ${row.full_name}.`);
          }
          if (payload.eventType === "UPDATE" && seededRef.current) {
            const oldRow = payload.old as VendorRow;
            const newRow = payload.new as VendorRow;
            if (oldRow?.payment_status !== "paid" && newRow?.payment_status === "paid") {
              tryBrowserNotify("Silver Sage Homeschool Hub", `Vendor payment received from ${newRow.full_name}.`);
            }
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "popup_early_bird_orders" },
        (payload) => {
          setTickets((prev) => {
            const row = (payload.new || payload.old) as TicketRow;
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as TicketRow;
            else next.unshift(payload.new as TicketRow);
            return next;
          });
          if (payload.eventType === "INSERT" && seededRef.current) {
            const row = payload.new as TicketRow;
            tryBrowserNotify("Silver Sage Homeschool Hub", `New early bird ticket purchase from ${row.full_name}.`);
          }
          if (payload.eventType === "UPDATE" && seededRef.current) {
            const oldRow = payload.old as TicketRow;
            const newRow = payload.new as TicketRow;
            if (oldRow?.payment_status !== "paid" && newRow?.payment_status === "paid") {
              tryBrowserNotify("Silver Sage Homeschool Hub", `Ticket payment received from ${newRow.full_name}.`);
            }
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "class_payments" },
        (payload) => {
          setClassPayments((prev) => {
            const row = (payload.new || payload.old) as ClassPaymentRow;
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as ClassPaymentRow;
            else next.unshift(payload.new as ClassPaymentRow);
            return next;
          });
          if (seededRef.current) {
            const row = (payload.new || payload.old) as ClassPaymentRow;
            const oldRow = payload.old as ClassPaymentRow | undefined;
            const status = (row?.payment_status ?? "").toLowerCase();
            if (payload.eventType === "INSERT" && status !== "paid") {
              tryBrowserNotify(
                "Silver Sage Homeschool Hub",
                `New class registration: ${row.adult_name ?? row.email} — ${classTitle(row.class_id)}`,
              );
            }
            const oldStatus = (oldRow?.payment_status ?? "").toLowerCase();
            if (oldStatus !== status) {
              if (status === "paid" || status === "complete") {
                tryBrowserNotify(
                  "Silver Sage Homeschool Hub",
                  `Class payment received: ${row.adult_name ?? row.email} — ${classTitle(row.class_id)}`,
                );
              } else if (status === "failed") {
                tryBrowserNotify(
                  "Silver Sage Homeschool Hub",
                  `Class payment FAILED: ${row.adult_name ?? row.email} — ${classTitle(row.class_id)}`,
                );
              }
            }
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "class_interest" },
        (payload) => {
          setClassInterest((prev) => {
            const row = (payload.new || payload.old) as ClassInterestRow;
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as ClassInterestRow;
            else next.unshift(payload.new as ClassInterestRow);
            return next;
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "admin_read_markers" },
        (payload) => {
          setReadMarkers((prev) => {
            const next = new Set(prev);
            const row = (payload.new || payload.old) as any;
            const k = markerKey(row.item_type, row.item_id);
            if (payload.eventType === "DELETE") next.delete(k);
            else next.add(k);
            return next;
          });
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "email_send_log" },
        (payload) => {
          const row = (payload.new || payload.old) as EmailLogRow;
          setEmailLogs((prev) => {
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as EmailLogRow;
            else next.unshift(payload.new as EmailLogRow);
            return next;
          });
          if (seededRef.current && payload.eventType === "INSERT") {
            const newRow = payload.new as EmailLogRow;
            const status = (newRow.status ?? "").toLowerCase();
            if (["failed", "dlq", "bounced", "complained"].includes(status)) {
              if (isSenderDomainError(newRow.error_message)) {
                tryBrowserNotify(
                  "Silver Sage Homeschool Hub",
                  `Sender domain error — automated emails are blocked. ${newRow.recipient_email}`,
                );
              } else {
                tryBrowserNotify(
                  "Silver Sage Homeschool Hub",
                  `Email failed to ${newRow.recipient_email}: ${newRow.error_message ?? "unknown error"}`,
                );
              }
            } else if (status === "sent" && newRow.resend_of_message_id) {
              tryBrowserNotify(
                "Silver Sage Homeschool Hub",
                `Resent email delivered to ${newRow.recipient_email}.`,
              );
            }
          }
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "instructor_proposals" },
        (payload) => {
          setProposals((prev) => {
            const row = (payload.new || payload.old) as ProposalRow;
            if (payload.eventType === "DELETE") return prev.filter((r) => r.id !== row.id);
            const next = [...prev];
            const idx = next.findIndex((r) => r.id === row.id);
            if (idx >= 0) next[idx] = payload.new as ProposalRow;
            else next.unshift(payload.new as ProposalRow);
            return next;
          });
          if (payload.eventType === "INSERT" && seededRef.current) {
            const row = payload.new as ProposalRow;
            tryBrowserNotify(
              "Silver Sage Homeschool Hub",
              `New instructor proposal from ${row.full_name}: ${row.proposed_class_name}`,
            );
          }
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // ============ Derived: capacity notifications (deduped) ============
  // For each class, count paid registrations. When the class hits the near-full
  // threshold or reaches capacity, emit a synthetic notification. Dedupe via
  // admin_read_markers so we don't notify twice for the same threshold event.
  const capacityNotifications: AdminNotification[] = useMemo(() => {
    const paidByClass: Record<string, ClassPaymentRow[]> = {};
    for (const p of classPayments) {
      const status = (p.payment_status ?? "").toLowerCase();
      if (status !== "paid" && status !== "complete") continue;
      (paidByClass[p.class_id] = paidByClass[p.class_id] || []).push(p);
    }
    const out: AdminNotification[] = [];
    for (const [classId, list] of Object.entries(paidByClass)) {
      const enrolled = list.length;
      const remaining = CLASS_CAPACITY_DEFAULT - enrolled;
      const latestPaid = list
        .map((r) => r.created_at)
        .sort()
        .slice(-1)[0];
      if (enrolled >= CLASS_CAPACITY_DEFAULT) {
        out.push({
          id: `class_capacity:${classId}:full`,
          itemType: "class_capacity",
          itemId: `${classId}:full`,
          kind: "class_full",
          title: "Class Full",
          customerName: classTitle(classId),
          detail: `${enrolled} of ${CLASS_CAPACITY_DEFAULT} spots filled`,
          paymentStatus: "full",
          createdAt: latestPaid ?? new Date().toISOString(),
          read: false,
          classId,
        });
      } else if (remaining > 0 && remaining <= nearFullThreshold) {
        out.push({
          id: `class_capacity:${classId}:near${nearFullThreshold}`,
          itemType: "class_capacity",
          itemId: `${classId}:near${nearFullThreshold}`,
          kind: "class_near_full",
          title: "Class Nearly Full",
          customerName: classTitle(classId),
          detail: `Only ${remaining} spot${remaining !== 1 ? "s" : ""} remaining`,
          paymentStatus: "near_full",
          createdAt: latestPaid ?? new Date().toISOString(),
          read: false,
          classId,
        });
      }
    }
    return out;
  }, [classPayments, nearFullThreshold]);

  // Dedup email logs by message_id — keep only latest row per message
  const latestEmailByMessage: EmailLogRow[] = useMemo(() => {
    const seen = new Set<string>();
    const out: EmailLogRow[] = [];
    for (const e of emailLogs) {
      const key = e.message_id ?? e.id;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(e);
    }
    return out;
  }, [emailLogs]);

  // Detect stuck pending (>10min) as failures
  const emailNotifications: AdminNotification[] = useMemo(() => {
    const cutoff = Date.now() - 10 * 60 * 1000;
    return latestEmailByMessage
      .map((e) => {
        const status = (e.status ?? "").toLowerCase();
        if (status === "pending" && new Date(e.created_at).getTime() < cutoff) {
          return {
            id: `email:${e.message_id ?? e.id}`,
            itemType: "email" as const,
            itemId: e.message_id ?? e.id,
            kind: "email_stuck" as const,
            title: "Email Stuck in Queue",
            customerName: e.recipient_email,
            detail: `${emailTypeLabel(e.template_name)} — pending for over 10 minutes`,
            paymentStatus: "pending",
            createdAt: e.created_at,
            read: false,
            messageId: e.message_id ?? undefined,
            emailTemplate: e.template_name,
            emailAttemptCount: e.attempt_count ?? 1,
            relatedItemType: e.related_item_type,
            relatedItemId: e.related_item_id,
          } as AdminNotification;
        }
        return buildEmailNotification(e);
      })
      .filter((n): n is AdminNotification => n !== null);
  }, [latestEmailByMessage]);

  // Build merged notification list
  const proposalNotifications: AdminNotification[] = useMemo(
    () =>
      proposals.map((p) => ({
        id: `instructor_proposal:${p.id}`,
        itemType: "instructor_proposal" as const,
        itemId: p.id,
        kind: "proposal_new" as const,
        title: "New Instructor Proposal",
        customerName: p.full_name,
        detail: `${p.proposed_class_name} — ${p.email}`,
        paymentStatus: p.status,
        createdAt: p.created_at,
        read: p.review_status === "Reviewed",
      })),
    [proposals],
  );

  const notifications: AdminNotification[] = useMemo(() => {
    const all: AdminNotification[] = [
      ...vendors.map(buildVendorNotification),
      ...tickets.map(buildTicketNotification),
      ...classPayments.map(buildClassPaymentNotification),
      ...classInterest.map(buildClassInterestNotification),
      ...capacityNotifications,
      ...emailNotifications,
      ...proposalNotifications,
    ].map((n) => {
      // Vendor unread state is driven by review_status, NOT by admin_read_markers.
      // This keeps vendor notifications in history but reflects the review workflow
      // for the Pop-Up Management badge count.
      if (n.itemType === "popup_vendor") {
        const v = vendors.find((row) => row.id === n.itemId);
        return { ...n, read: v ? v.review_status === "Reviewed" : false };
      }
      if (n.itemType === "instructor_proposal") {
        // read state already computed from proposals.review_status
        return n;
      }
      return { ...n, read: readMarkers.has(`${n.itemType}:${n.itemId}`) };
    });
    return all.sort((a, b) => (a.createdAt < b.createdAt ? 1 : -1));
  }, [vendors, tickets, classPayments, classInterest, capacityNotifications, emailNotifications, proposalNotifications, readMarkers]);

  const isCountable = (n: AdminNotification) =>
    n.kind !== "vendor_canceled" && n.kind !== "class_canceled";

  const popupUnread = notifications.filter(
    (n) => !n.read && isCountable(n) && (n.itemType === "popup_vendor" || n.itemType === "popup_ticket"),
  ).length;

  const classUnread = notifications.filter(
    (n) =>
      !n.read &&
      isCountable(n) &&
      (n.itemType === "payment" || n.itemType === "interest" || n.itemType === "class_capacity"),
  ).length;

  const paymentsUnread = notifications.filter(
    (n) => !n.read && isCountable(n) && n.itemType === "payment",
  ).length;

  const interestUnread = notifications.filter(
    (n) => !n.read && isCountable(n) && n.itemType === "interest",
  ).length;

  const proposalsUnread = notifications.filter(
    (n) => !n.read && isCountable(n) && n.itemType === "instructor_proposal",
  ).length;

  const totalUnread = notifications.filter((n) => !n.read && isCountable(n)).length;

  const setVendorReviewStatus = useCallback(
    async (vendorId: string, status: "New" | "Reviewed"): Promise<{ success: boolean; error?: string }> => {
      const patch =
        status === "Reviewed"
          ? { review_status: "Reviewed" as const, reviewed_at: new Date().toISOString(), reviewed_by: userId ?? null }
          : { review_status: "New" as const, reviewed_at: null, reviewed_by: null };
      setVendors((prev) => prev.map((v) => (v.id === vendorId ? { ...v, ...patch } : v)));
      const { error } = await supabase
        .from("popup_vendor_registrations")
        .update(patch)
        .eq("id", vendorId);
      if (error) {
        fetchAll();
        return { success: false, error: error.message };
      }
      return { success: true };
    },
    [userId, fetchAll],
  );

  const markAsRead = useCallback(
    async (itemType: string, itemId: string) => {
      // Vendor "read" is driven by review_status — treat markAsRead as
      // "mark reviewed" for popup_vendor items.
      if (itemType === "popup_vendor") {
        await setVendorReviewStatus(itemId, "Reviewed");
        return;
      }
      if (itemType === "instructor_proposal") {
        const patch = { review_status: "Reviewed", reviewed_at: new Date().toISOString(), reviewed_by: userId ?? null };
        setProposals((prev) => prev.map((p) => (p.id === itemId ? { ...p, ...patch } as ProposalRow : p)));
        await (supabase.from("instructor_proposals" as any).update(patch).eq("id", itemId) as any);
        return;
      }
      const key = markerKey(itemType, itemId);
      if (readMarkers.has(key)) return;
      setReadMarkers((prev) => new Set(prev).add(key));
      const { error } = await supabase
        .from("admin_read_markers")
        .insert({ item_type: itemType, item_id: itemId, read_by: userId ?? null });
      if (error && !String(error.message).toLowerCase().includes("duplicate")) {
        setReadMarkers((prev) => {
          const n = new Set(prev);
          n.delete(key);
          return n;
        });
      }
    },
    [readMarkers, userId, setVendorReviewStatus],
  );


  const bulkSetVendorReviewStatus = useCallback(
    async (vendorIds: string[], status: "New" | "Reviewed") => {
      if (vendorIds.length === 0) return { success: true, count: 0 };
      const patch =
        status === "Reviewed"
          ? { review_status: "Reviewed" as const, reviewed_at: new Date().toISOString(), reviewed_by: userId ?? null }
          : { review_status: "New" as const, reviewed_at: null, reviewed_by: null };
      setVendors((prev) => prev.map((v) => (vendorIds.includes(v.id) ? { ...v, ...patch } : v)));
      const { error } = await supabase
        .from("popup_vendor_registrations")
        .update(patch)
        .in("id", vendorIds);
      if (error) {
        fetchAll();
        return { success: false, error: error.message, count: 0 };
      }
      return { success: true, count: vendorIds.length };
    },
    [userId, fetchAll],
  );

  const markAllPopupRead = useCallback(async () => {
    // Vendors: mark all countable unread vendors as Reviewed.
    const vendorIds = vendors
      .filter((v) => v.registration_status !== "canceled" && v.review_status !== "Reviewed")
      .map((v) => v.id);
    if (vendorIds.length > 0) {
      await bulkSetVendorReviewStatus(vendorIds, "Reviewed");
    }
    // Tickets: continue using admin_read_markers.
    const unreadTickets = notifications.filter(
      (n) => !n.read && isCountable(n) && n.itemType === "popup_ticket",
    );
    if (unreadTickets.length > 0) {
      const optimistic = new Set(readMarkers);
      unreadTickets.forEach((n) => optimistic.add(`${n.itemType}:${n.itemId}`));
      setReadMarkers(optimistic);
      const rows = unreadTickets.map((n) => ({
        item_type: n.itemType,
        item_id: n.itemId,
        read_by: userId ?? null,
      }));
      await supabase.from("admin_read_markers").upsert(rows, {
        onConflict: "item_type,item_id",
        ignoreDuplicates: true,
      });
    }
  }, [vendors, notifications, readMarkers, userId, bulkSetVendorReviewStatus]);

  const markAllRead = useCallback(async () => {
    // Vendors via review_status
    const vendorIds = vendors
      .filter((v) => v.registration_status !== "canceled" && v.review_status !== "Reviewed")
      .map((v) => v.id);
    if (vendorIds.length > 0) {
      await bulkSetVendorReviewStatus(vendorIds, "Reviewed");
    }
    // Everything else via admin_read_markers
    const unread = notifications.filter(
      (n) => !n.read && isCountable(n) && n.itemType !== "popup_vendor",
    );
    if (unread.length === 0) return;
    const optimistic = new Set(readMarkers);
    unread.forEach((n) => optimistic.add(`${n.itemType}:${n.itemId}`));
    setReadMarkers(optimistic);
    const rows = unread.map((n) => ({
      item_type: n.itemType,
      item_id: n.itemId,
      read_by: userId ?? null,
    }));
    await supabase.from("admin_read_markers").upsert(rows, {
      onConflict: "item_type,item_id",
      ignoreDuplicates: true,
    });
  }, [vendors, notifications, readMarkers, userId, bulkSetVendorReviewStatus]);

  // ============ Stats ============
  // vendorNew = vendors flagged as needing review (review_status = 'New',
  // excluding canceled). This drives the Pop-Up Management badge count.
  const vendorNew = vendors.filter(
    (v) => v.registration_status !== "canceled" && v.review_status !== "Reviewed",
  ).length;
  const vendorPending = vendors.filter((v) => v.payment_status === "pending").length;
  const vendorConfirmed = vendors.filter((v) => v.registration_status === "confirmed").length;
  const ticketsSold = tickets
    .filter((t) => t.payment_status === "paid")
    .reduce((s, t) => s + t.ticket_quantity, 0);
  const ticketsAwaiting = tickets.filter(
    (t) => t.payment_status === "paid" && t.ticket_status !== "sent",
  ).length;
  const popupRevenue =
    vendors.filter((v) => v.payment_status === "paid").reduce((s, v) => s + v.amount_due, 0) +
    tickets.filter((t) => t.payment_status === "paid").reduce((s, t) => s + t.amount_due, 0);

  // Class stats — exclude canceled from active totals
  const activeClass = classPayments.filter(
    (p) => (p.payment_status ?? "").toLowerCase() !== "canceled",
  );
  const classNew = activeClass.filter(
    (p) => !readMarkers.has(`payment:${p.id}`),
  ).length;
  const classPending = activeClass.filter(
    (p) => ["pending", "unpaid", ""].includes((p.payment_status ?? "").toLowerCase()),
  ).length;
  const classPaidConfirmed = activeClass.filter((p) =>
    ["paid", "complete"].includes((p.payment_status ?? "").toLowerCase()),
  ).length;
  const classFailed = activeClass.filter(
    (p) => (p.payment_status ?? "").toLowerCase() === "failed",
  ).length;
  const classRevenue = activeClass
    .filter((p) => ["paid", "complete"].includes((p.payment_status ?? "").toLowerCase()))
    .reduce((s, p) => s + (p.amount_paid ?? 0), 0);
  const classesNearCapacity = capacityNotifications.length;
  const waitlistedStudents = 0;

  // ============ Email stats ============
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  const emailFailed = latestEmailByMessage.filter((e) =>
    ["failed", "dlq", "bounced", "complained"].includes((e.status ?? "").toLowerCase()),
  ).length;
  const emailPending = latestEmailByMessage.filter(
    (e) => (e.status ?? "").toLowerCase() === "pending",
  ).length;
  const emailSentToday = latestEmailByMessage.filter(
    (e) => (e.status ?? "").toLowerCase() === "sent" && new Date(e.created_at) >= todayStart,
  ).length;
  const emailResendsNeeded = emailNotifications.filter(
    (n) => n.kind === "email_failed" || n.kind === "email_stuck" || n.kind === "sender_domain_error",
  ).length;
  const senderDomainBlocked = latestEmailByMessage.some(
    (e) => isSenderDomainError(e.error_message) &&
      new Date(e.created_at).getTime() > Date.now() - 24 * 60 * 60 * 1000,
  );

  const [resending, setResending] = useState<Set<string>>(new Set());
  const resendEmail = useCallback(
    async (messageId: string): Promise<{ success: boolean; error?: string }> => {
      if (!messageId) return { success: false, error: "Missing messageId" };
      if (resending.has(messageId)) return { success: false, error: "Resend in progress" };
      setResending((prev) => new Set(prev).add(messageId));
      try {
        const { data, error } = await supabase.functions.invoke("admin-resend-email", {
          body: { messageId },
        });
        if (error) return { success: false, error: error.message ?? "Resend failed" };
        if ((data as any)?.error) return { success: false, error: (data as any).error };
        return { success: true };
      } catch (e: any) {
        return { success: false, error: e?.message ?? String(e) };
      } finally {
        setResending((prev) => {
          const n = new Set(prev);
          n.delete(messageId);
          return n;
        });
      }
    },
    [resending],
  );

  return {
    loading,
    notifications,
    popupUnread,
    classUnread,
    paymentsUnread,
    interestUnread,
    proposalsUnread,
    totalUnread,
    markAsRead,
    markAllPopupRead,
    markAllRead,
    refresh: fetchAll,
    nearFullThreshold,
    setNearFullThreshold,
    resendEmail,
    isResending: (messageId: string) => resending.has(messageId),
    setVendorReviewStatus,
    bulkSetVendorReviewStatus,
    stats: {
      vendorNew,
      vendorPending,
      vendorConfirmed,
      ticketsSold,
      ticketsAwaiting,
      popupRevenue,
      classNew,
      classPending,
      classPaidConfirmed,
      classFailed,
      classRevenue,
      classesNearCapacity,
      waitlistedStudents,
      emailFailed,
      emailPending,
      emailSentToday,
      emailResendsNeeded,
      senderDomainBlocked,
    },
  };
}
